import SeoHead from "@/components/SeoHead";
import { useTranslation } from "react-i18next";
import { Mail, MessageCircle, HelpCircle, User, Settings, ArrowLeft, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function SupportPage() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyEmail = () => {
    const email = "bamabang.info@gmail.com";
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SeoHead
        path="support"
        title={t("support.seo_title")}
        description={t("support.seo_desc")}
      />

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center text-gray-600 hover:text-pink-600 transition">
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="font-medium hidden sm:inline">{t("common.back", "Back")}</span>
          </Link>
          <div className="flex items-center gap-3">
            <img src="/favicon.png" alt="Logo" className="w-16 h-16" />
            <h1 className="font-bold text-gray-900 text-lg hidden sm:block">BamaBang</h1>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-600 to-rose-500 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block p-4 bg-white/20 backdrop-blur-md rounded-2xl mb-6 shadow-xl">
             <img src="/favicon.png" alt="App Icon" className="w-20 h-20 rounded-2xl shadow-lg" />
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            {t("support.title")}
          </h2>
          <p className="text-pink-50 text-lg opacity-90 max-w-2xl mx-auto">
            {t("support.subtitle")}
          </p>
        </div>
      </div>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-12 -mt-8">
        <div className="grid gap-8">
          
          {/* Contact Section */}
          <section className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-8 border border-gray-100">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-pink-100 text-pink-600 rounded-xl">
                <MessageCircle className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{t("support.contact_title")}</h3>
            </div>
            
            <p className="text-gray-600 mb-8 leading-relaxed">
              {t("support.contact_desc")}
            </p>

            <div className="bg-gray-50 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-gray-100 hover:border-pink-200 transition group relative">
              <div 
                className="flex items-center gap-4 cursor-pointer"
                onClick={copyEmail}
                title="Click to copy"
              >
                <div className="p-3 bg-white text-pink-600 rounded-full shadow-sm group-hover:scale-110 transition">
                  {copied ? <Check className="w-5 h-5 text-green-500" /> : <Mail className="w-5 h-5" />}
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium flex items-center gap-2">
                    {t("support.email_label")}
                    {copied && <span className="text-green-500 text-xs font-bold animate-bounce">Copied!</span>}
                  </p>
                  <p className="text-lg font-bold text-gray-900 select-all group-hover:text-pink-600 transition">
                    bamabang.info@gmail.com
                  </p>
                </div>
              </div>
              <button 
                onClick={() => {
                  window.location.href = "mailto:bamabang.info@gmail.com";
                }}
                className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-3 rounded-xl font-bold text-center transition shadow-lg shadow-pink-200 active:scale-95"
              >
                {t("support.contact_title")}
              </button>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-8 border border-gray-100">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-indigo-100 text-indigo-600 rounded-xl">
                <HelpCircle className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{t("support.faq_title")}</h3>
            </div>

            <div className="space-y-6">
              {[
                { q: "faq_q1", a: "faq_a1", icon: <User className="w-5 h-5" /> },
                { q: "faq_q2", a: "faq_a2", icon: <MessageCircle className="w-5 h-5" /> },
                { q: "faq_q3", a: "faq_a3", icon: <User className="w-5 h-5" /> },
                { q: "faq_q4", a: "faq_a4", icon: <Settings className="w-5 h-5" /> }
              ].map((item, index) => (
                <div key={index} className="group border-b border-gray-100 last:border-0 pb-6 last:pb-0">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1 text-gray-400 group-hover:text-pink-500 transition">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-pink-600 transition">
                        {t(`support.${item.q}`)}
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {t(`support.${item.a}`)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <img src="/favicon.png" alt="Logo" className="w-10 h-10 mx-auto mb-4 opacity-50 grayscale" />
          <p className="text-sm">© 2026 bamabang.net. All rights reserved.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-white transition">{t("footer.privacy")}</Link>
            <Link to="/terms" className="hover:text-white transition">{t("footer.terms")}</Link>
            <Link to="/refund" className="hover:text-white transition font-semibold text-pink-400 hover:text-pink-300">
              {t("footer.refund", "Refund & Cancellation Policy")}
            </Link>
          </div>
          <p className="mt-4 text-xs">
            Contact Us / お問い合わせ: bamabang.info@gmail.com
          </p>
        </div>
      </footer>
    </div>
  );
}
